import { useState } from 'react';
import styled from 'styled-components';
import { mapi } from './api';
import { Button, ButtonRow, Chip, Field, Hint, Muted, NeutralChip, Warn } from '../ui/primitives';
import type { CustomerRow, Suggestion } from './types';
import { Osio } from './Osio';

/* Uuden asiakkaan käyttöönotto: AI lukee yrityksen verkkosivut ja ehdottaa
   yritysprofiilin, toimialat ja viestipohjat. Ehdotus näytetään ensin ja
   kirjoitetaan asiakkaan kantaan vasta hyväksynnän jälkeen — malli voi
   ymmärtää yrityksen väärin, ja väärä profiili ohjaisi kaikkia viestejä. */

const Kentta = styled.div`
  margin-bottom: 10px;
  .otsikko { font-size: 12px; font-weight: 700; text-transform: uppercase;
             letter-spacing: .04em; color: ${p => p.theme.color.muted}; }
  pre {
    margin: 3px 0 0;
    white-space: pre-wrap;
    font: inherit;
    font-size: 13px;
  }
`;

interface Props {
  asiakas: CustomerRow;
  onToast: (v: string) => void;
  onDone: () => void;
}

const PROFIILI_NIMET: Record<string, string> = {
  yritys_kuvaus: 'Mitä yritys tekee',
  yritys_tuotteet: 'Tuotteet tai palvelut',
  yritys_kohdeasiakkaat: 'Ihanteellinen asiakas',
  yritys_myyntiargumentit: 'Myyntiargumentit',
  yritys_referenssit: 'Referenssit',
  yritys_tavoitteet: 'Tavoitteet',
  yritys_savy: 'Viestien sävy',
  yritys_valta: 'Mitä AI ei saa tehdä',
};

export function SetupCard({ asiakas, onToast, onDone }: Props) {
  const [sivu, setSivu] = useState(asiakas.website || '');
  const [ajossa, setAjossa] = useState(false);
  const [ehdotus, setEhdotus] = useState<Suggestion | null>(null);
  const [tallentaa, setTallentaa] = useState(false);

  const valmis = asiakas.profile?.valmis;

  const luo = async () => {
    setAjossa(true);
    setEhdotus(null);
    try {
      const r = await mapi<{ suggestion: Suggestion }>(
        `/api/master/customers/${asiakas.id}/generate`, 'POST', { website: sivu.trim() });
      setEhdotus(r.suggestion);
      onToast('Ehdotus valmis — tarkista se ennen tallennusta');
    } catch (err) { onToast(err instanceof Error ? err.message : String(err)); }
    setAjossa(false);
  };

  const tallenna = async () => {
    if (!ehdotus) return;
    setTallentaa(true);
    try {
      const r = await mapi<{ kentat: number; toimialat: number; pohjat: number }>(
        `/api/master/customers/${asiakas.id}/generate/apply`, 'POST', { suggestion: ehdotus });
      onToast(`Tallennettu: ${r.kentat} profiilikenttää, ${r.toimialat} toimialaa, `
        + `${r.pohjat} viestipohjaa`);
      setEhdotus(null);
      onDone();
    } catch (err) { onToast(err instanceof Error ? err.message : String(err)); }
    setTallentaa(false);
  };

  return (
    <Osio>
      <h4>
        Käyttöönotto AI:lla
        {valmis ? <Chip $bg="#5C6B54">Profiili valmis</Chip> : <NeutralChip>Profiili kesken</NeutralChip>}
      </h4>
      <Hint style={{ marginTop: 4 }}>
        AI lukee yrityksen verkkosivut ja ehdottaa yritysprofiilin, toimialat ja
        viestipohjat. Ehdotus tallennetaan vasta kun hyväksyt sen, eikä se koskaan
        korvaa asiakkaan itse kirjoittamia tekstejä.
      </Hint>

      <Field>
        <label>Yrityksen verkkosivu</label>
        <input
          value={sivu} placeholder="https://www.yritys.fi"
          onChange={e => setSivu(e.target.value)}
        />
      </Field>

      <ButtonRow>
        <Button $variant="primary" disabled={ajossa} onClick={luo}>
          {ajossa ? 'AI tutkii yritystä…' : ehdotus ? 'Luo uusi ehdotus' : '✨ Luo ehdotus'}
        </Button>
        {ajossa && <Muted>Verkkohaku kestää 1–3 minuuttia. Älä sulje ikkunaa.</Muted>}
      </ButtonRow>

      {valmis && !ehdotus && (
        <Hint>
          Asiakkaalla on jo profiili. Esitäyttö täydentää vain tyhjät kentät ja
          lisää puuttuvat viestipohjat.
        </Hint>
      )}

      {ehdotus && (
        <>
          <h4 style={{ margin: '14px 0 6px' }}>Ehdotus</h4>
          {Object.entries(PROFIILI_NIMET).map(([k, nimi]) => (
            ehdotus.profile[k] ? (
              <Kentta key={k}>
                <div className="otsikko">{nimi}</div>
                <pre>{ehdotus.profile[k]}</pre>
              </Kentta>
            ) : null
          ))}

          <Kentta>
            <div className="otsikko">Toimialat ({ehdotus.industries.length})</div>
            <pre>{ehdotus.industries.map(t => `${t.label} — ${t.aliases.join(', ')}`).join('\n')}</pre>
          </Kentta>

          <Kentta>
            <div className="otsikko">Viestipohjat ({ehdotus.templates.length})</div>
            <pre>{ehdotus.templates.map(t => `${t.name}: ${t.subject}`).join('\n')}</pre>
          </Kentta>

          {!!ehdotus.sources?.length && (
            <Kentta>
              <div className="otsikko">Lähteet</div>
              <pre>{ehdotus.sources.join('\n')}</pre>
            </Kentta>
          )}

          <Warn>
            Tarkista erityisesti tuotteet ja referenssit. AI ei saa keksiä asiakkaita
            eikä lukuja, mutta se voi ymmärtää sivun väärin — virheellinen profiili
            ohjaa kaikkia myöhempiä viestejä.
          </Warn>

          <ButtonRow>
            <Button $variant="primary" disabled={tallentaa} onClick={tallenna}>
              {tallentaa ? 'Tallennetaan…' : 'Hyväksy ja tallenna asiakkaalle'}
            </Button>
            <Button onClick={() => setEhdotus(null)}>Hylkää</Button>
          </ButtonRow>
          <Hint>
            Asiakkaan ylläpitäjä muokkaa yritysprofiilia ja toimialoja. Viestipohjia voi muokata Viestipohjat-välilehdellä.
          </Hint>
        </>
      )}
    </Osio>
  );
}
